// RUTA: src/components/support/ErrorWithTicket.tsx
//
// UI estándar para mostrar un error al usuario con la opción de
// crear un ticket de soporte automáticamente. Reusable en cualquier
// parte del sistema (cliente, coach, admin).
//
// Uso típico:
//
//   {error && (
//     <ErrorWithTicket
//       title="No pudimos cargar tus rutinas"
//       message={error.message}
//       onRetry={() => refetch()}
//       ticketContext={{ section: 'rutinas', error }}
//     />
//   )}

import { useState } from 'react';
import { AlertTriangle, Loader2, LifeBuoy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { createTicketAuto } from '@/lib/createTicket';
import { TicketCreatedDialog } from './TicketCreatedDialog';

interface Props {
  /** Título corto del error (ej. "No pudimos cargar X") */
  title: string;
  /** Mensaje técnico — opcional. Si se pasa, se muestra al usuario y
   *  va dentro de la descripción del ticket. */
  message?: string;
  /** Callback opcional de "Reintentar" */
  onRetry?: () => void;
  /** Contexto extra para el ticket — incluye datos relevantes para
   *  que el admin pueda diagnosticar (ej. id del cliente, nombre de
   *  la sección, payload que se intentó enviar). */
  ticketContext?: {
    /** Etiqueta corta de dónde sucedió el error (sección, feature) */
    section?: string;
    /** Cualquier dato extra a serializar en el ticket */
    extra?: Record<string, unknown>;
  };
  /** Tamaño visual — default 'normal'. 'compact' es para errores
   *  inline pequeños (en cards, listas). */
  variant?: 'normal' | 'compact';
}

export function ErrorWithTicket({
  title,
  message,
  onRetry,
  ticketContext,
  variant = 'normal',
}: Props) {
  const [creating, setCreating] = useState(false);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCreateTicket = async () => {
    setCreating(true);
    const sectionLabel = ticketContext?.section || 'unknown';
    const result = await createTicketAuto({
      title: `[${sectionLabel}] ${title}`,
      description: message
        ? `Error mostrado al usuario:\n\n${message}\n\n**Sección:** ${sectionLabel}`
        : `Error en sección **${sectionLabel}**.`,
      category: 'bug',
      captureScreenshot: true,
      extra: ticketContext?.extra,
    });
    setCreating(false);
    if (result) {
      setCreatedNumber(result.ticket_number);
      setConfirmOpen(true);
    } else {
      toast.error('No se pudo crear el ticket. ¿Tienes sesión activa?');
    }
  };

  const compact = variant === 'compact';

  return (
    <>
      <div className={`rounded-xl border-2 border-destructive/30 bg-destructive/5 ${compact ? 'p-3' : 'p-4 md:p-5'}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className={`shrink-0 text-destructive ${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
          <div className="min-w-0 flex-1">
            <p className={`font-bold text-destructive ${compact ? 'text-xs' : 'text-sm'}`}>
              {title}
            </p>
            {message && (
              <p className={`text-muted-foreground mt-1 break-words ${compact ? 'text-[11px]' : 'text-xs'}`}>
                {message}
              </p>
            )}

            <div className={`flex flex-wrap gap-2 ${compact ? 'mt-2' : 'mt-3'}`}>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary hover:bg-border text-foreground text-[11px] font-bold uppercase tracking-wider transition-colors min-h-[32px]"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Reintentar
                </button>
              )}
              <button
                onClick={handleCreateTicket}
                disabled={creating || createdNumber !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent hover:bg-accent/90 text-accent-foreground text-[11px] font-bold uppercase tracking-wider transition-colors min-h-[32px] disabled:opacity-60"
              >
                {creating
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creando…</>
                  : createdNumber
                    ? <>✓ Ticket {createdNumber}</>
                    : <><LifeBuoy className="w-3.5 h-3.5" /> Crear ticket</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      <TicketCreatedDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        ticketNumber={createdNumber}
      />
    </>
  );
}
